import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import NoteIcon from "./NoteIcon";
import NoteModal from "./NoteModal";
import NoteTooltip from "./NoteTooltip";
import { loadNotes } from "../../../store/features/dealProgress/noteActions";

const DealProgressBar = ({ stages, businessDetails }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [hoveredStage, setHoveredStage] = useState(null);
  const [selectedStageForNote, setSelectedStageForNote] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const ctxObj = JSON.parse(sessionStorage.getItem("userDetails"))?.result;
  const role = JSON.parse(sessionStorage.getItem("userDetails"))?.result;

  // Get user role from Redux store
  const userRole = useSelector((state) => state.auth.user?.role);
  const notes = useSelector((state) => state.notes.notesByStage);
  const notesLoading = useSelector((state) => state.notes.isLoading);

  // Load notes when component mounts or businessDetails changes
  useEffect(() => {
    if (businessDetails?.id) {
      dispatch(loadNotes(businessDetails.id));
    }
  }, [dispatch, businessDetails?.id]);

  // Convert stages object to array and sort by id
  const stagesArray = Object.values(stages).sort((a, b) => a.id - b.id);
  const countComplete = stagesArray.filter(
    (stage) => stage.status === "completed"
  ).length;
  const currentStageIndex = stagesArray.findIndex(
    (stage) => stage.status === "current"
  );
  const calculatedCurrentStep =
    currentStageIndex !== -1 ? currentStageIndex + 1 : countComplete;
  const totalSteps = stagesArray.length;
  const progressPercentage = Math.round(
    ((calculatedCurrentStep - 1) / totalSteps) * 100
  );

  const getStage = (stageName) => {
    if (stageName === "Assigned To Partner") {
      return "assignedToPartner";
    }
    if (stageName === "Profile Submitted") {
      return "profileSubmitted";
    }
    if (stageName === "In Evaluation") {
      return "inNegotiation";
    }
    if (stageName === "Buyer Match") {
      return "buyerMatch";
    }
    if (stageName === "Completed") {
      return "completed";
    }
    return "pending";
  };

  const handleNoteIconClick = (stage, event) => {
    event.stopPropagation();
    setSelectedStageForNote(stage);
    setShowNoteModal(true);
  };

  const handleCloseModal = () => {
    setShowNoteModal(false);
    setSelectedStageForNote(null);
  };

  const getNotesForStage = (stageId) => {
    return notes[stageId] || {};
  };

  // Check if user has permission to see notes
  const hasNotePermission =
    role?.role?.id === 1 ||
    role?.role?.id === 2 ||
    role?.role?.id === 4 ||
    role?.role?.id === 5 ||
    (role?.role?.id === 3 && ctxObj?.loggedinprofiletype === "sellerprofile");

  return (
    <div className="container">
      <div className="tracker-header container mt-4 mb-4">
        <h3 className="tracker-title">{t("dealProgress")}</h3>
        <div className="progress-summary">
          <span className="step-counter">
            {calculatedCurrentStep - 1} of {totalSteps} {t("steps")}
          </span>
          <span className="percentage">{progressPercentage}%</span>
          <span className="status-text">{t("inProgress")}</span>
        </div>
      </div>

      <div className="progress-line">
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="progress-steps">
          {stagesArray.map((stage, index) => {
            const isCompleted = stage.status === "completed";
            const isCurrent = stage.status === "current";
            const isPending = stage.status === "pending";
            const stageNotes = getNotesForStage(stage.id);

            return (
              <div
                key={stage.id}
                className={`progress-step ${stage.status}`}
                onMouseEnter={() => setHoveredStage(stage.id)}
                onMouseLeave={() => setHoveredStage(null)}
              >
                <div className="step-icon">
                  {isCompleted ? (
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="check-icon"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <div>{index + 1}</div>
                  )}
                </div>

                <NoteIcon
                  notes={stageNotes}
                  userRole={userRole}
                  onClick={(event) => handleNoteIconClick(stage, event)}
                />

                <h4 className="step-title">{t(getStage(stage.title))}</h4>

                {/* Existing tooltip */}
                {hasNotePermission && hoveredStage === stage.id && (
                  <div className="hover-tooltip">
                    <div className="tooltip-content">
                      <div className="tooltip-title">
                        {t(getStage(stage.title))}
                      </div>
                      {stage.description && (
                        <div className="tooltip-meta">{stage.description}</div>
                      )}
                      {stage.partner && (
                        <div className="tooltip-meta">
                          Partner: {stage.partner.name}
                        </div>
                      )}
                      {stage.estimatedTime && isPending && (
                        <div className="tooltip-meta">
                          Estimated: {stage.estimatedTime}
                        </div>
                      )}
                      {stage.since && (
                        <div className="tooltip-meta">Since: {stage.since}</div>
                      )}
                      {stage.pendingDocs !== undefined && (
                        <div className="tooltip-meta">
                          Documents: {stage.pendingDocs} pending
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Note Tooltip */}
                {hoveredStage === stage.id && (
                  <NoteTooltip
                    notes={stageNotes}
                    userRole={userRole}
                    stageName={t(getStage(stage.title))}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Note Modal */}
      {showNoteModal && selectedStageForNote && (
        <NoteModal
          stage={selectedStageForNote}
          stageName={t(getStage(selectedStageForNote.title))}
          notes={getNotesForStage(selectedStageForNote.id)}
          userRole={userRole}
          onClose={handleCloseModal}
          businessDetails={businessDetails}
        />
      )}
    </div>
  );
};

export default DealProgressBar;
